import { cn } from '@invent/wl-ui-kit-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { CompactMenu, NotPermitted, PageHeader, type DataTableState, type IDataSelection } from '@/design';
import { useCan } from '@/domains/auth';
import type { IEtagged } from '@/domains/api';
import type {
  IListField,
  IListFieldCatalogue,
  IListFormattingRule,
  IListOrder,
  IListQuery,
  IListRow,
  IListScope,
  IMigratedPart,
  ISavedView
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, Pager, PastTheEnd, useAdminData } from '../../components/admin-page';
import {
  NAME_FIELD,
  NO_PROBLEMS,
  criteriaPaths,
  isChanged,
  placeProblems,
  readCriteria,
  writeCriteria,
  type ICriteriaGroup,
  type ICriteriaProblem,
  type IFilterChip
} from '../../list-criteria';
import {
  ALL_SURVEYS,
  DEFAULT_LIST_PAGE_SIZE,
  LIST_PAGE_SIZES,
  LIST_SURVEYS,
  type IListSurvey,
  TEAMS_SURVEY,
  TEAM_PATH_CHOICES,
  catalogueSections,
  countOf,
  formatListValue,
  listSurveyLabel,
  lostSummary,
  migratedPartLabel
} from '../../lists';
import { runSaid, type IRecycleRun } from '../../recycle-bin';
import { ListActions } from './actions';
import { AdvancedFilterSheet } from './advanced-filter';
import { ExportDialog } from './export-dialog';
import { FormattingSheet, ruleProblems } from './formatting-sheet';
import { RowNotesDialog } from './row-notes';
import { SaveAsReportDialog, useViewSaves } from './save-view';
import type { ICellEdit } from './cells';
import { ColumnsSheet } from './columns-sheet';
import { ConflictDialog } from './conflict-dialog';
import {
  ASSIGNED_TO,
  answerTagOf,
  draftKey,
  editKindOf,
  isAnswerField,
  nameOf,
  useListEdits
} from './editing';
import { FilterChips, FiltersClear, NameContainsBox } from './filter-chips';
import { hasFunnel } from './funnel';
import { ListGrid, type IFunnelOpen } from './grid';
import { GroupStrip } from './group-by';
import { Legend } from './legend';
import { ReadingPane, ReadingPaneToggle, type PaneMode } from './pane';
import { ListToolbar } from './toolbar';
import { useCatalogueLookups, useCriteriaWords } from './use-catalogue';
import { rowKeyOf, useListRows } from './use-list-rows';
import { ViewPicker } from './view-picker';

/**
 * Lists (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3) — the
 * Conga grid the research team runs the cycle in, live over survey-api's list engine: a survey's
 * saved views, a funnel on every column, the filter in words, grouping, Σ totals and a 30 s poll.
 * It replaces the demo's Worklists mock (SR-221), whose shape and words it keeps.
 *
 * **The address is the survey and the view** (§3.1): `?survey=` one of `LIST_SURVEYS` or `all`, FA
 * when absent; `?view=` a view's id. With no `view` the survey's default view opens while the
 * caller has opened none of the survey, else the one opened last — survey-api lists the opened ones
 * first, newest first. A `view` the caller cannot open says "No such view" and opens the default.
 * So a link to a view is shareable and the back button walks views; **nothing else of the screen is
 * in the address** — a filter, a column or a sort changed is page state, marked *Changed — not
 * saved*, and a reload loses it, as Conga's `*Unsaved View` did.
 *
 * **A survey this deployment does not hold** — its catalogue answers 404; `local` has no Canada —
 * draws its tab and "This survey is not on this deployment", and nothing else.
 *
 * **One screen per view opened** (`ListScreen`, keyed by the view and its `ETag`): what a reader
 * changed belongs to the view it was changed on, and a view opened afresh starts from what it holds.
 * **Cells being edited are not dropped unasked** (the owner's ruling, 2026-10-09): while drafts are
 * pending, opening another view (the picker, "Select a view") or another survey's tab asks first —
 * "Discard N unsaved changes?", *Discard* or *Cancel*, which keeps the screen and its drafts. The
 * survey tab already open is no link at all: following it would drop the view and open the screen
 * afresh.
 *
 * **The writes** (§3.6, §3.9, §3.10): an editable cell's control stages a draft (`editing.ts`),
 * *Save data* writes them as one batch and asks about each conflict in turn, *Actions ▾* stages a
 * value on the ticked rows or sets one on every row, or moves their responses to the recycle bin
 * (SR-470's D6) — the moved rows leave on the page read afresh, and the outcome is said in the
 * status line — and the reading pane reads a row as a record or edits it. **Every decision reads
 * `useCan()`** (§3.12): without `internal_fields.write` every cell is read, *Save data* and
 * *Edit* are disabled and one `NotPermitted` says why; without `responses.annotate` an answer's
 * cell is read, said the same way (SR-403); without `lists.assign` the *Assigned to* cells are
 * read and say so too.
 *
 * **The view's saves** (§3.3, `save-view.tsx`) are taken in place: a *Save*, *Rename…*, *Make
 * default* or *Reload* replaces the view the screen compares with, and *Save as…* or *Delete…* hands
 * the screen to the new view, or to none, without opening it afresh (`ListOpening`'s `hand`) — so the
 * cells being edited, the page and the pane stay. *Formatting…* edits the screen's rules; *Export…*
 * and *Save as a report…* are *Actions ▾*'s (§3.10, §3.11).
 */
export function AdminListsPage() {
  const [params, setParams] = useSearchParams();
  const guard = useDraftsGuard();
  const survey = params.get('survey') || 'FA';
  const viewParam = params.get('view');
  // The catalogue's 404 is an answer — this survey is not here — so the read says which survey it
  // answered, and `null` inside it is that answer rather than a read still out.
  const catalogue = useAdminData(
    async (signal) => ({ survey, fields: await listsApi.fields(survey, signal) }),
    [survey]
  );
  const answer = catalogue.data?.survey === survey ? catalogue.data : null;

  return (
    <>
      <PageHeader
        title="Lists"
        crumb="Cycle"
        tabs={<SurveyTabs current={survey} guard={guard.ask} />}
      />
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {catalogue.error ? (
          <LoadError message={`The list’s fields could not be read. ${catalogue.error}`} />
        ) : answer === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            Reading the list…
          </p>
        ) : answer.fields === null ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            This survey is not on this deployment.
          </p>
        ) : (
          <ListOpening
            key={survey}
            survey={survey}
            viewParam={viewParam}
            catalogue={answer.fields}
            guard={guard}
            onOpenView={(viewId, viewSurvey = survey) =>
              setParams({ survey: viewSurvey, view: viewId }, { replace: viewSurvey !== survey })
            }
            onMove={(viewId) => setParams(viewId ? { survey, view: viewId } : { survey })}
          />
        )}
      </div>
      {guard.dialog}
    </>
  );
}

/** The drafts' guard (the owner's ruling): the screen says how many cells are pending, and a move
 *  that would drop them asks first. */
interface IDraftsGuard {
  /** Runs `go` at once while nothing is pending, else once *Discard* is pressed. */
  ask: (go: () => void) => void;
  /** The screen's pending count, told on each change. */
  pending: (count: number) => void;
  dialog: React.ReactNode;
}

function useDraftsGuard(): IDraftsGuard {
  const pendingRef = React.useRef(0);
  const [asking, setAsking] = React.useState<Nullable<{ count: number; go: () => void }>>(null);
  const ask = React.useCallback((go: () => void) => {
    if (pendingRef.current > 0) setAsking({ count: pendingRef.current, go });
    else go();
  }, []);
  const pending = React.useCallback((count: number) => {
    pendingRef.current = count;
  }, []);
  const dialog = (
    <AlertDialog open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard {countOf(asking?.count ?? 0, 'unsaved change')}?</AlertDialogTitle>
          <AlertDialogDescription>
            {asking?.count === 1 ? 'The cell is' : 'The cells are'} not saved — Save data writes
            them. Opening another view or survey drops {asking?.count === 1 ? 'it' : 'them'}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const go = asking?.go;
              setAsking(null);
              go?.();
            }}
          >
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { ask, pending, dialog };
}

/** The survey tabs (LST-04) as routed links — each drops the view, which is the survey's own. A
 *  `survey` in the address no tab knows gets a tab of its own, spelled as sent. With a `guard`
 *  (Lists), a press goes through it, and the tab already open does nothing. `surveys` narrows the
 *  tabs where a screen reads fewer — Reinstatement, *All surveys* and Canada aside. `searchOf`
 *  writes a tab's address where a screen keeps the rest of its own (DD calls), `replace` in place. */
export function SurveyTabs({
  current,
  guard,
  surveys = [...LIST_SURVEYS, ALL_SURVEYS],
  searchOf = (code) => `?survey=${encodeURIComponent(code)}`,
  replace
}: {
  /** null: no tab is open — Reports' search, which reads every survey (SR-462). */
  current: Nullable<string>;
  guard?: (go: () => void) => void;
  surveys?: readonly IListSurvey[];
  searchOf?: (code: string) => string;
  replace?: boolean;
}) {
  const navigate = useNavigate();
  const known = surveys;
  const tabs = current === null || known.some((tab) => tab.code === current)
    ? known
    : [...known, { code: current, label: listSurveyLabel(current) }];
  return (
    <nav aria-label="Surveys" className="-mx-1 flex flex-wrap gap-x-4">
      {tabs.map((tab) => {
        const active = tab.code === current;
        const to = { search: searchOf(tab.code) };
        return (
          <Link
            key={tab.code}
            to={to}
            replace={replace}
            onClick={(event) => {
              const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
              if (!guard || !plain) return;
              event.preventDefault();
              if (!active) guard(() => navigate(to));
            }}
            aria-current={active ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex items-baseline gap-1.5 border-b-2 px-1 pt-1 pb-2 text-[0.8125rem] transition-colors',
              active
                ? 'border-[var(--color-teal)] font-medium text-[var(--color-royal-blue)]'
                : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** `SurveyTabs` in a work page's compact bar: the survey open, the others a menu away. */
export function SurveyMenu({
  current,
  surveys = [...LIST_SURVEYS, ALL_SURVEYS],
  searchOf = (code) => `?survey=${encodeURIComponent(code)}`,
  replace
}: {
  current: string;
  surveys?: readonly IListSurvey[];
  searchOf?: (code: string) => string;
  replace?: boolean;
}) {
  return (
    <CompactMenu
      label="Survey"
      trigger={listSurveyLabel(current)}
      items={surveys.map((entry) => ({
        key: entry.code,
        label: entry.label,
        to: { search: searchOf(entry.code) },
        replace,
        current: entry.code === current
      }))}
    />
  );
}

/** The view a survey's Lists open on, and whether the one the address named could not be — or,
 *  where it is another survey's view, that survey, whose tab it opens under. */
interface IOpened {
  view: Nullable<IEtagged<ISavedView>>;
  noSuchView: boolean;
  elsewhere?: string;
  /** The screen drawn for this opening — a view handed over in place keeps the screen it came
   *  from. */
  key: number;
}

/**
 * Which view opens (§3.1): the address's, else the one opened last, else the default. The open is
 * survey-api's `GET /views/{id}`, which stamps it — so it is asked once per address, and the
 * picker's list, read when it opens, already has it first. A view of another survey — a link
 * pasted without its `survey` — moves the address to that survey's tab, in place.
 *
 * **A view handed over is not opened again** (`hand`): *Save as…* made it and *Delete…* left none,
 * and the screen already shows what it holds, cells being edited included. The address moves to it
 * — the back button still walks views — and the read that the move asks for answers the view handed
 * over, under the screen it came from, so nothing remounts.
 */
function ListOpening({
  survey,
  viewParam,
  catalogue,
  guard,
  onOpenView,
  onMove
}: {
  survey: string;
  viewParam: Nullable<string>;
  catalogue: IListFieldCatalogue;
  guard: IDraftsGuard;
  onOpenView: (viewId: string, viewSurvey?: string) => void;
  /** The address to a view handed over, or to none — no read of it. */
  onMove: (viewId: Nullable<string>) => void;
}) {
  const handed = React.useRef<Nullable<{ viewId: Nullable<string>; opened: IOpened }>>(null);
  const screens = React.useRef(0);
  const opened = useAdminData<IOpened>(
    async (signal) => {
      const held = handed.current;
      if (held !== null && held.viewId === viewParam) return held.opened;
      handed.current = null;
      const key = ++screens.current;
      let noSuchView = false;
      if (viewParam !== null) {
        const asked = await listsApi.openView(viewParam, signal);
        if (asked !== null && asked.body.query.survey !== survey) {
          return { view: null, noSuchView, elsewhere: asked.body.query.survey, key };
        }
        if (asked !== null) return { view: asked, noSuchView, key };
        noSuchView = true;
      }
      const views = (await listsApi.listViews(survey, 'list', signal)) ?? [];
      // The list is survey-api's order: the opened ones first, newest first.
      const last = noSuchView ? undefined : views.find((view) => view.openedAt !== null);
      const pick = last ?? views.find((view) => view.isDefault);
      const view = pick ? await listsApi.openView(pick.id, signal) : null;
      return { view, noSuchView, key };
    },
    [survey, viewParam]
  );
  const hand = (view: Nullable<IEtagged<ISavedView>>) => {
    const current = opened.data;
    if (current === null) return;
    const next: IOpened = { view, noSuchView: false, key: current.key };
    handed.current = { viewId: view?.body.id ?? null, opened: next };
    opened.adopt(next);
    onMove(view?.body.id ?? null);
  };

  const elsewhere = opened.data?.elsewhere;
  React.useEffect(() => {
    if (elsewhere !== undefined && viewParam !== null) onOpenView(viewParam, elsewhere);
    // Once per answer: the address moves, and this component goes with the old survey's tab.
  }, [elsewhere]);

  if (opened.error) {
    return <LoadError message={`This list could not be opened. ${opened.error}`} />;
  }
  if (opened.data === null || elsewhere !== undefined) {
    return (
      <p role="status" className="py-8 text-center text-muted-foreground">
        Opening the view…
      </p>
    );
  }
  const { view, noSuchView } = opened.data;
  return (
    <>
      {noSuchView && (
        <p role="alert" className="text-sm text-destructive">
          No such view — {view ? 'the survey’s default view is open instead.' : 'and this survey has no default view.'}
        </p>
      )}
      <ListScreen
        key={opened.data.key}
        survey={survey}
        catalogue={catalogue}
        opened={view}
        guard={guard}
        onOpenView={onOpenView}
        onHand={hand}
      />
    </>
  );
}

/** survey-api's *Survey* field — the column *All surveys* opens with (§3.2 item 1). */
export const SURVEY_FIELD = 'sys.survey';

/** The columns a list opens with where no view opens: survey-api's seeded views' own core
 *  (`ListViewSeeds` — Name, Firm, State, Status) and the day submitted, with on *All surveys* each
 *  row's survey. A name alone looked like an empty screen; a field the catalogue lacks is left out. */
const BARE_COLUMNS = ['sys.firm_name', 'sys.state', 'sys.status', 'sys.submitted_at'];

/** What the screen starts from: the view's query, page size and rules — or, for a survey with no
 *  view opened and no default, and for a new report (`ReportScreen`), a bare list (`BARE_COLUMNS`).
 *  A view's own columns are its own, none included. */
export function startOf(view: Nullable<ISavedView>, fields: ReadonlyMap<string, IListField>, survey: string) {
  const query = view?.query;
  const surveyColumn = survey === ALL_SURVEYS.code ? [SURVEY_FIELD] : [];
  const bare = [NAME_FIELD, ...surveyColumn, ...BARE_COLUMNS].filter((code) => fields.has(code));
  return {
    root: readCriteria(query?.criteria ?? null),
    columns: view ? (query?.columns ?? []) : bare,
    sorts: query?.sorts ?? [],
    groupBy: query?.groupBy ?? [],
    scope: query?.scope ?? null,
    pageSize: view?.pageSize ?? null,
    rules: view?.formattingRules ?? []
  };
}

/**
 * One view on screen: the filter, columns, sorts, grouping and page size as the reader changed
 * them — the view's until they differ, then *Changed — not saved* — and the page read under them.
 */
function ListScreen({
  survey,
  catalogue,
  opened,
  guard,
  onOpenView,
  onHand
}: {
  survey: string;
  catalogue: IListFieldCatalogue;
  /** The view as opened, with its `ETag`; the screen then holds it, and its saves replace it. */
  opened: Nullable<IEtagged<ISavedView>>;
  /** Told the pending drafts; asked before another view opens. */
  guard: IDraftsGuard;
  onOpenView: (viewId: string) => void;
  /** A view made by *Save as…*, or none after *Delete…*, handed to the address in place. */
  onHand: (view: Nullable<IEtagged<ISavedView>>) => void;
}) {
  const [saved, setSaved] = React.useState(opened);
  const view = saved?.body ?? null;
  const { fields, fieldOf, sourceOf, kindOf, assignees, accounts, emailOf, words: lookupWords } =
    useCatalogueLookups(catalogue);
  // The accounts' read failing is said, and nothing is assigned over it (§3.6).
  const accountsUnread = assignees.error !== null;

  const [start] = React.useState(() => startOf(view, fields, survey));
  const [root, setRoot] = React.useState<ICriteriaGroup>(start.root);
  const words = useCriteriaWords(survey, root, lookupWords);
  const [columns, setColumns] = React.useState<string[]>(start.columns);
  const [sorts, setSorts] = React.useState<IListOrder[]>(start.sorts);
  const [groupBy, setGroupBy] = React.useState<IListOrder[]>(start.groupBy);
  const [scope, setScope] = React.useState<Nullable<IListScope>>(start.scope);
  const [pageSize, setPageSize] = React.useState<Nullable<number>>(start.pageSize);
  const [rules, setRules] = React.useState<IListFormattingRule[]>(start.rules);
  const [formatting, setFormatting] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);
  const [noting, setNoting] = React.useState<Nullable<IListRow>>(null);
  const [page, setPage] = React.useState(1);
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const [advanced, setAdvanced] = React.useState<{ open: boolean; focusId?: string }>({ open: false });

  /** A change of what the list shows starts again at page 1: page 3 of the old list means nothing. */
  const change =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const query: IListQuery = {
    survey,
    scope,
    criteria: writeCriteria(root, kindOf),
    columns,
    sorts,
    groupBy
  };
  const changed =
    view !== null && isChanged(view, { query, pageSize, formattingRules: rules });

  // The read asks for every field its cells need: the columns, and what a grouping or a rule reads.
  // The name too, whatever the view shows: a selection, the pane and a conflict name the person.
  const needed = [
    ...columns,
    NAME_FIELD,
    ...groupBy.map((level) => level.field),
    ...rules.flatMap((rule) => (rule.field ? [rule.field] : []))
  ].filter((code, index, all) => fields.has(code) && all.indexOf(code) === index);
  const extra = needed.filter((code) => !columns.includes(code));
  const readQuery: IListQuery = { ...query, columns: [...columns, ...extra] };
  const shownColumns = columns.flatMap((code) => fields.get(code) ?? []);
  const hiddenColumns = columns.filter((code) => !fields.has(code)).length;
  const sums = shownColumns.filter((field) => field.summable);
  const size = pageSize ?? DEFAULT_LIST_PAGE_SIZE;

  const list = useListRows({
    query: readQuery,
    page,
    pageSize: size,
    sums: sums.map((field) => field.code)
  });
  const { read, error } = list;

  const can = useCan();
  const viewSaves = useViewSaves({
    kind: 'list',
    survey,
    saved,
    changed,
    screen: () => ({ query, pageSize, formattingRules: rules }),
    pageSize: size,
    canShare: can('lists.share'),
    sayProblem: (problem) => sayProblem(problem, query, fieldOf),
    onAdopt: setSaved,
    onCreated: (created) => {
      setSaved(created);
      // The screen is taken over in place, and the dialog's page size is the new view's — a view
      // saved with the size the screen was showing by default would otherwise read as changed.
      setPageSize(created.body.pageSize);
      onHand(created);
    },
    onDeleted: () => {
      setSaved(null);
      onHand(null);
    },
    onFormatting: () => setFormatting(true)
  });
  const canWrite = can('internal_fields.write');
  const canAssign = can('lists.assign');
  const canBulk = can('lists.bulk');
  // An answer cell is survey-api's correction, the Answers tab's own policy (SR-403).
  const canCorrect = can('responses.annotate');
  const writeReason = React.useId();
  const assignReason = React.useId();
  const correctReason = React.useId();
  const edits = useListEdits({
    viewId: view?.id ?? null,
    emailOf,
    adopt: list.adopt,
    poll: list.poll,
    refresh: list.refresh
  });
  const editOf = (row: IListRow, field: IListField): Nullable<ICellEdit> => {
    const kind = editKindOf(field);
    if (kind === null || !canWrite || (kind === 'assignee' && (!canAssign || accountsUnread))) return null;
    if (!canCorrect && isAnswerField(field)) return null;
    const key = draftKey({ ...row, field: field.code });
    return {
      kind,
      draft: edits.drafts.get(key),
      error: edits.cellErrors.get(key),
      accounts,
      onStage: (ui, badInput) => edits.stage(row, field, ui, badInput)
    };
  };

  // The ticked rows and what the last press said belong to the list read: a new query, page or
  // Refresh starts without them.
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [massSaid, setMassSaid] = React.useState<Nullable<string>>(null);
  const [recycleSaid, setRecycleSaid] = React.useState<Nullable<IRecycleRun>>(null);
  const { clearSaid } = edits;
  React.useEffect(() => {
    setTicked(new Set());
    setMassSaid(null);
    setRecycleSaid(null);
    clearSaid();
  }, [list.asked, clearSaid]);
  const selection: IDataSelection<IListRow> = {
    selected: ticked,
    onSelect: (keys) => setTicked(new Set(keys)),
    name: nameOf
  };
  const tickedRows = (read?.rows ?? []).filter((row) => ticked.has(rowKeyOf(row)));
  const editableSections = catalogueSections(
    catalogue,
    (field) => editKindOf(field) !== null && (canCorrect || !isAnswerField(field))
  );
  const answerShown = shownColumns.some((field) => answerTagOf(field) !== null);
  const answersLocked =
    !canCorrect && catalogue.groups.some((group) => group.fields.some((field) => answerTagOf(field) !== null));

  const [pane, setPane] = React.useState<Nullable<{ key: string; mode: PaneMode }>>(null);
  const [paneShown, setPaneShown] = React.useState(true);
  /** The pane's row where it is on this page, the pane drawn or hidden. */
  const lastPaneRow = pane ? (read?.rows.find((row) => rowKeyOf(row) === pane.key) ?? null) : null;
  const paneRow = paneShown ? lastPaneRow : null;
  const openPane = (row: IListRow, mode: PaneMode) => {
    setPane({ key: rowKeyOf(row), mode });
    setPaneShown(true);
  };
  /** *Reading pane*: hides the pane drawn, else draws one — the row last read where it is on this
   *  page, else the page's first. */
  const pressPane = () => {
    if (paneRow) {
      setPaneShown(false);
      return;
    }
    const row = lastPaneRow ?? read?.rows[0];
    if (row) openPane(row, pane?.mode ?? 'view');
  };
  /** ↑/↓ in the grid move the pane drawn with the focused row, in the mode it is in — only one
   *  drawn: a pane whose row left the page is hidden, and *Reading pane* says so. */
  const follow = (event: React.FocusEvent) => {
    const target = event.target as HTMLElement;
    const key = target.matches('tr[data-row-key]') ? target.getAttribute('data-row-key') : null;
    if (key !== null && paneRow && pane && key !== pane.key) setPane({ ...pane, key });
  };
  const pendingCount = edits.drafts.size;
  const { pending: tellPending } = guard;
  React.useEffect(() => {
    tellPending(pendingCount);
    return () => tellPending(0);
  }, [pendingCount, tellPending]);
  // What a mass update counts and an export says: this query's total, never the last query's while
  // this one's read is out — the press sends this query.
  const total = read !== null && read.asked === list.asked ? read.total : null;

  // The criteria a read or a save refused land on their chips and builder rows alike; the rest of a
  // save's refusals are said in its line (`viewSaves.notice`).
  const criteriaProblems: ICriteriaProblem[] = viewSaves.problems.filter((problem) =>
    problem.target.startsWith('query.criteria')
  );
  const otherProblems: string[] = [];
  for (const problem of error?.problems ?? []) {
    if (problem.target.startsWith('query.criteria')) criteriaProblems.push(problem);
    else otherProblems.push(sayProblem(problem, readQuery, fieldOf));
  }
  const placed =
    criteriaProblems.length > 0
      ? placeProblems(criteriaProblems, root, criteriaPaths(root))
      : NO_PROBLEMS;
  const problemsOf = (nodeId: string) => placed.byNode.get(nodeId) ?? [];
  const openAdvanced = (focusId?: string) => setAdvanced({ open: true, focusId });
  // Which column's funnel is open — the screen's, so a chip's press opens its column's (§3.4).
  const [funnel, setFunnel] = React.useState<Nullable<IFunnelOpen>>(null);
  const nameBox = React.useRef<HTMLInputElement>(null);
  /** A chip's press: its column's funnel where the grid draws one — closed again by a second
   *  press, as by its header's button — the name box for its own chip, else *Advanced…* on its
   *  node: a column not shown, or one that filters on nothing. */
  const pressChip = (chip: IFilterChip, from: HTMLElement) => {
    const column =
      chip.door === 'funnel' ? shownColumns.find((field) => field.code === chip.field) : undefined;
    if (chip.door === 'name') nameBox.current?.focus();
    else if (column && hasFunnel(column)) {
      setFunnel(funnel?.code === column.code ? null : { code: column.code, returnTo: from });
    } else openAdvanced(chip.nodeIds[0]);
  };

  const struckOnPage = read ? read.rows.filter((row) => list.struck.has(row.participantId)).length : 0;
  let tableState: DataTableState | undefined;
  if (read === null && error) {
    const said =
      error.problems.length > 0
        ? `This list could not be read: ${
            [...placed.unplaced.map((problem) => sayProblem(problem, readQuery, fieldOf)), ...otherProblems].join(
              '; '
            ) || 'the conditions marked cannot be used — change them or ✕ them.'
          }`
        : `This list could not be read. ${error.message}`;
    tableState = { kind: 'error', content: <LoadError message={said} /> };
  } else if (read === null) {
    tableState = { kind: 'empty', content: 'Reading the list…' };
  } else if (read.rows.length === 0 && read.total > 0) {
    tableState = {
      kind: 'empty',
      content: <PastTheEnd page={page} pageSize={size} total={read.total} counted={['row', 'rows']} onPage={setPage} />
    };
  } else if (read.rows.length === 0) {
    tableState = { kind: 'empty', content: 'No row matches this list.' };
  }

  return (
    <div className="grid min-w-0 gap-3">
      <ListToolbar
        writes={
          <>
            <Button
              type="button"
              className="h-8 px-3"
              disabled={!canWrite || pendingCount === 0 || edits.saving}
              aria-describedby={canWrite ? undefined : writeReason}
              onClick={() => {
                setTicked(new Set());
                void edits.save();
              }}
            >
              {edits.saving ? 'Saving…' : `Save data${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-8 px-2.5 font-normal"
              disabled={pendingCount === 0 || edits.saving}
              onClick={edits.discard}
            >
              Discard
            </Button>
          </>
        }
        actions={
          <ListActions
            survey={survey}
            query={query}
            viewId={view?.id ?? null}
            total={total}
            catalogue={editableSections}
            selectedRows={tickedRows}
            pageRows={read?.rows ?? []}
            accounts={accounts}
            canWrite={canWrite}
            canAssign={canAssign}
            canBulk={canBulk}
            canRecycle={canCorrect}
            canAccess={can('rankings.manage')}
            canReadCycles={can('cycles.manage')}
            staged={pendingCount > 0}
            saving={edits.saving}
            answersLocked={answersLocked}
            accountsUnread={accountsUnread}
            onStage={(rows, field, ui) => {
              for (const row of rows) edits.stage(row, field, ui);
            }}
            onSaveNow={(rows, field, ui) => {
              if (edits.saving) return;
              setTicked(new Set());
              void edits.saveNow(rows, field, ui);
            }}
            onMassUpdated={(said) => {
              setMassSaid(said);
              void list.poll();
            }}
            // The page afresh, not a poll: a poll strikes a row that left, and a moved row is gone.
            onRecycled={async (run) => {
              setRecycleSaid(run);
              setTicked(new Set());
              await list.refresh();
            }}
            onAccessChanged={setMassSaid}
            onExport={() => setExporting(true)}
            onSaveAsReport={() => setReporting(true)}
          />
        }
        pane={
          <ReadingPaneToggle
            shown={paneRow !== null}
            disabled={!read?.rows.length}
            onPress={pressPane}
          />
        }
        picker={
          <ViewPicker
            survey={survey}
            current={view ? { id: view.id, name: view.name } : null}
            changed={changed}
            onOpen={(viewId) => guard.ask(() => onOpenView(viewId))}
            onDeleted={(viewId) => {
              if (viewId !== view?.id) return;
              setSaved(null);
              onHand(null);
            }}
          />
        }
        changed={changed}
        saveView={viewSaves.menu}
        nameBox={<NameContainsBox inputRef={nameBox} root={root} onChange={change(setRoot)} />}
        filters={<FiltersClear root={root} words={words} onChange={change(setRoot)} />}
        columns={<ColumnsSheet catalogue={catalogue} columns={columns} onChange={change(setColumns)} />}
        onRefresh={() => void list.refresh()}
        readAt={read?.readAt ?? null}
        reading={list.reading}
      />

      {viewSaves.notice}
      {!canWrite && <NotPermitted id={writeReason} what="editing cells in a list" />}
      {canWrite && !canAssign && columns.includes(ASSIGNED_TO) && (
        <NotPermitted id={assignReason} what="assigning rows — Assigned to is read here" />
      )}
      {canWrite && !canCorrect && answerShown && (
        <NotPermitted id={correctReason} what="correcting survey answers — the answers are read here" />
      )}
      {accountsUnread && (
        <p role="alert" className="text-sm text-destructive">
          The accounts to assign could not be read — Assigned to is read here. {assignees.error}
        </p>
      )}
      {edits.banner && (
        <p role="alert" className="text-sm text-destructive">
          {edits.banner}
        </p>
      )}

      {view?.migratedFrom && <MigratedLine view={view} />}

      {survey === TEAMS_SURVEY && (
        <TeamPathChoice
          path={scope?.path ?? null}
          onChoose={(path) => change(setScope)({ ...(scope ?? {}), path })}
        />
      )}

      <FilterChips
        root={root}
        words={words}
        onChange={change(setRoot)}
        onChipPress={pressChip}
        onAdvanced={openAdvanced}
        problemsOf={problemsOf}
      />

      <GroupStrip
        groupBy={groupBy}
        columns={columns}
        fieldOf={fieldOf}
        aggregates={read?.aggregates ?? null}
        onChange={change(setGroupBy)}
      />

      {error && read !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error.message}`} />
      )}
      {list.pollError && read !== null && !error && (
        <p role="status" className="text-sm text-muted-foreground">
          Could not refresh — showing the list as last read. {list.pollError}
        </p>
      )}
      {columns.length === 0 && (
        <p className="text-sm text-muted-foreground">This view shows no columns — add them from Columns.</p>
      )}
      {hiddenColumns > 0 && (
        <p className="text-sm text-muted-foreground">
          This view names {hiddenColumns === 1 ? 'a column' : `${hiddenColumns} columns`} you cannot
          see; {hiddenColumns === 1 ? 'it is' : 'they are'} left out.
        </p>
      )}
      <p role="status" aria-live="polite" className="text-sm text-muted-foreground empty:hidden">
        {[
          edits.status ? `${edits.status}.` : null,
          massSaid ? `${massSaid}.` : null,
          recycleSaid ? runSaid(recycleSaid) : null,
          struckOnPage > 0
            ? `${struckOnPage} ${struckOnPage === 1 ? 'row no longer matches' : 'rows no longer match'} this list.`
            : null,
          list.entered > 0
            ? `${list.entered} ${list.entered === 1 ? 'row joined' : 'rows joined'} this list — Refresh to see ${list.entered === 1 ? 'it' : 'them'}.`
            : null
        ]
          .filter(Boolean)
          .join(' ')}
      </p>

      {/* The pane's column only while a pane is drawn: reserved always, it left the grid a blank
          beside it on a wide screen. Its width follows the screen's — at least 30.6rem from 90rem —
          so its controls are not held to a fixed 26rem. */}
      <div
        className={cn(
          'grid items-start gap-3',
          paneRow && pane && 'min-[90rem]:grid-cols-[minmax(0,1fr)_min(34vw,40rem)]'
        )}
      >
      <div className="grid min-w-0 gap-3" onFocus={follow}>
      <ListGrid
        label={`${listSurveyLabel(survey)} list`}
        columns={shownColumns}
        fieldOf={fieldOf}
        sourceOf={sourceOf}
        rows={read?.rows ?? null}
        state={tableState}
        struck={list.struck}
        sorts={sorts}
        onSort={change(setSorts)}
        root={root}
        query={readQuery}
        kindOf={kindOf}
        words={words}
        filtering={{
          onApply: change(setRoot),
          onAdvanced: () => openAdvanced(),
          open: funnel,
          onOpen: setFunnel
        }}
        groupBy={groupBy}
        collapsed={collapsed}
        onToggleGroup={(key) =>
          setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
        aggregates={read?.aggregates ?? null}
        sums={sums}
        rules={rules}
        editOf={editOf}
        selection={selection}
        actions={{
          onView: (row) => openPane(row, 'view'),
          onEdit: (row) => openPane(row, 'edit'),
          onNote: setNoting,
          editLocked: canWrite ? null : writeReason
        }}
        open={{
          key: paneRow ? rowKeyOf(paneRow) : null,
          onOpen: (row) => openPane(row, pane?.mode ?? 'view')
        }}
      />

      <div
        data-grid-footer=""
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.8125rem] text-muted-foreground"
      >
        <Legend rules={rules} words={words} />
        {read?.aggregatesError ? (
          <span>The totals could not be read: {read.aggregatesError}</span>
        ) : (
          sums.map((field) => {
            const sum = read?.aggregates?.sums[field.code];
            return (
              <span key={field.code} data-total={field.code} className="tabular-nums">
                Σ {field.label}{' '}
                <span className="font-medium text-foreground">
                  {read?.aggregates ? (sum === null || sum === undefined ? '—' : formatListValue(field, sum)) : ''}
                </span>
              </span>
            );
          })
        )}
        <span className="flex-1" />
        <label className="flex items-center gap-1.5">
          Per page
          <select
            value={size}
            onChange={(event) => change(setPageSize)(Number(event.target.value))}
            className="h-7 rounded-md border border-border bg-white px-1.5 text-[0.8125rem] text-foreground"
          >
            {[...new Set([...LIST_PAGE_SIZES, size])].sort((a, b) => a - b).map((option) => (
              <option key={option} value={option}>
                {option.toLocaleString('en-US')}
              </option>
            ))}
          </select>
        </label>
        {read && (
          <Pager page={page} pageSize={size} total={read.total} onPage={setPage} busy={list.reading} />
        )}
      </div>
      </div>
      {paneRow && pane && (
        <ReadingPane
          row={paneRow}
          columns={shownColumns}
          catalogue={catalogue}
          mode={pane.mode}
          onMode={(mode) => setPane({ ...pane, mode })}
          onClose={() => setPane(null)}
          editOf={editOf}
          editLocked={canWrite ? null : writeReason}
        />
      )}
      </div>

      <ConflictDialog
        asked={edits.asked}
        fieldOf={fieldOf}
        accounts={accounts}
        onKeepMine={(asked) => void edits.keepMine(asked)}
        onTakeTheirs={edits.takeTheirs}
      />

      <FormattingSheet
        open={formatting}
        onOpenChange={setFormatting}
        rules={rules}
        catalogue={catalogue}
        words={words}
        kindOf={kindOf}
        accounts={assignees.data ?? undefined}
        problems={ruleProblems(viewSaves.problems)}
        onApply={(next) => {
          setRules(next);
          viewSaves.clearProblems();
        }}
      />

      <ExportDialog
        open={exporting}
        onClose={() => setExporting(false)}
        survey={survey}
        query={query}
        shownColumns={shownColumns.length}
        total={total}
      />

      <SaveAsReportDialog open={reporting} onClose={() => setReporting(false)} survey={survey} query={query} />
      {noting && (
        <RowNotesDialog
          row={noting}
          onAppended={(cell) => list.adopt([cell])}
          onClose={() => setNoting(null)}
        />
      )}

      <AdvancedFilterSheet
        open={advanced.open}
        onOpenChange={(open) => setAdvanced((current) => ({ ...current, open }))}
        root={root}
        catalogue={catalogue}
        words={words}
        placed={placed}
        accounts={assignees.data ?? undefined}
        focusId={advanced.focusId}
        onApply={change(setRoot)}
      />
    </div>
  );
}

/** A query's member a refusal names by index: its words, and the field code at that index. */
const QUERY_MEMBERS: Readonly<
  Record<'columns' | 'sorts' | 'groupBy', { what: string; code: (query: IListQuery, index: number) => string | undefined }>
> = {
  columns: { what: 'The column', code: (query, index) => query.columns?.[index] },
  sorts: { what: 'The sort by', code: (query, index) => query.sorts?.[index]?.field },
  groupBy: { what: 'The grouping by', code: (query, index) => query.groupBy?.[index]?.field }
};

/** A refusal no chip carries, in words: the column, sort or grouping it names by its label. */
export function sayProblem(
  problem: ICriteriaProblem,
  query: IListQuery,
  fieldOf: (code: string) => IListField | undefined
): string {
  const at = /^query\.(columns|sorts|groupBy)\[(\d+)\]/.exec(problem.target);
  if (at) {
    const member = QUERY_MEMBERS[at[1] as keyof typeof QUERY_MEMBERS];
    const code = member.code(query, Number(at[2]));
    return `${member.what} ${code ? (fieldOf(code)?.label ?? code) : ''} ${problem.reason}`.replace(/\s+/g, ' ');
  }
  if (problem.target.startsWith('query.scope.path')) return `The team path ${problem.reason}`;
  if (problem.target.startsWith('query.criteria')) return `A condition ${problem.reason}`;
  return problem.reason;
}

/** The Teams survey's *Team path* chip: All teams, Wirehouse, Independent. */
export function TeamPathChoice({
  path,
  onChoose
}: {
  path: Nullable<string>;
  onChoose: (path: Nullable<string>) => void;
}) {
  const choices = [
    { code: null as Nullable<string>, label: 'All teams' },
    ...TEAM_PATH_CHOICES,
    ...(path !== null && !TEAM_PATH_CHOICES.some((choice) => choice.code === path)
      ? [{ code: path, label: path }]
      : [])
  ];
  return (
    <div role="radiogroup" aria-label="Team path" className="flex flex-wrap items-center gap-1.5 text-[0.8125rem]">
      <span className="mr-1 text-xs font-medium text-[var(--veritant-royal-70)]">Team path</span>
      {choices.map((choice) => {
        const on = choice.code === path;
        return (
          <button
            key={choice.code ?? ''}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChoose(choice.code)}
            className={cn(
              'h-7 rounded-full border px-3 outline-none focus-visible:ring-2 focus-visible:ring-ring',
              on
                ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] font-medium text-[var(--color-royal-blue)]'
                : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A migrated view's line (§3.3; survey-api's §12): "Carried over from Salesforce — 3 columns and 1
 * condition could not be carried. What was not carried" — the popover listing each part survey-api
 * names, its Salesforce name and survey-api's reason as sent. **Only a part marked lost is counted**
 * (SR-408's R1, the owner's Q1): since !103 the list also names parts carried with nothing lost —
 * listed apart, under "Carried with nothing lost", never counted — and a part without the marker
 * (a view migrated before it, an API before it) is a loss, as every part was. The reason is never
 * read to decide which. The view opens and works with what was carried; the line is there so staff
 * repair it.
 */
export function MigratedLine({ view }: { view: ISavedView }) {
  const parts = view.migratedFrom?.unmapped ?? [];
  const lost = parts.filter((part) => part.lost !== false);
  const kept = parts.filter((part) => part.lost === false);
  return (
    <p data-migrated="" className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
      <span>
        Carried over from Salesforce
        {migratedEnding(lost, kept)}
      </span>
      {parts.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="link" className="h-auto p-0 text-sm">
              What was not carried
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            aria-label="What was not carried"
            className="grid max-h-[70dvh] w-96 max-w-[calc(100vw-2rem)] gap-3 overflow-y-auto p-3 text-[0.8125rem]"
          >
            {lost.length > 0 && <MigratedParts parts={lost} name="Lost" />}
            {kept.length > 0 && (
              <div className="grid gap-1.5">
                <p className="text-xs font-medium tracking-wide text-[var(--veritant-royal-70)] uppercase">
                  Carried with nothing lost
                </p>
                <MigratedParts parts={kept} name="Carried with nothing lost" />
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </p>
  );
}

/** How the migrated line's sentence ends: what was lost, else that nothing was, else a full stop
 *  (a view whose `unmapped` names no part). */
function migratedEnding(lost: readonly IMigratedPart[], kept: readonly IMigratedPart[]): string {
  if (lost.length > 0) return ` — ${lostSummary(lost)} could not be carried.`;
  if (kept.length > 0) return ' with nothing lost.';
  return '.';
}

/** One list of a migrated view's parts — each its part, its Salesforce name, survey-api's reason. */
function MigratedParts({ parts, name }: { parts: readonly IMigratedPart[]; name: string }) {
  return (
    <ul aria-label={name} className="grid gap-2">
      {parts.map((part, index) => (
        <li key={index} className="grid gap-0.5">
          <span className="font-medium">
            {migratedPartLabel(part.part)}
            {salesforceName(part.sf) && (
              <span className="font-normal text-muted-foreground"> — {salesforceName(part.sf)}</span>
            )}
          </span>
          {part.reason && <span className="text-muted-foreground">{part.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Salesforce's name for a part, as survey-api kept it — a string as sent, an object's string
 *  members joined; never a document. */
function salesforceName(sf: unknown): string {
  if (typeof sf === 'string' || typeof sf === 'number' || typeof sf === 'boolean') return String(sf);
  if (sf !== null && typeof sf === 'object' && !Array.isArray(sf)) {
    return Object.values(sf)
      .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
      .join(' · ');
  }
  return '';
}
